import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { RootState } from '../../store/rootReducer';
import {
  selectUpdateWorkspaceLoading,
  selectUpdateWorkspaceError,
  selectUpdateWorkspaceSuccess,
} from '../../store/workspace/workspace.selector';
import {
  updateWorkspaceRequest,
  clearWorkspaceState,
} from '../../store/workspace/workspace.action';
import { useToast } from '../../contexts/ToastContext';

interface UpdateWorkspaceNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
}

const UpdateWorkspaceNameModal: React.FC<UpdateWorkspaceNameModalProps> = ({
  isOpen,
  onClose,
  currentName,
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [workspaceName, setWorkspaceName] = useState(currentName);
  const [nameError, setNameError] = useState('');

  const updateWorkspaceLoading = useSelector((state: RootState) =>
    selectUpdateWorkspaceLoading(state)
  );
  const updateWorkspaceError = useSelector((state: RootState) =>
    selectUpdateWorkspaceError(state)
  );
  const updateWorkspaceSuccess = useSelector((state: RootState) =>
    selectUpdateWorkspaceSuccess(state)
  );

  useEffect(() => {
    if (isOpen) {
      setWorkspaceName(currentName);
      setNameError('');
    }
  }, [isOpen, currentName]);

  useEffect(() => {
    if (updateWorkspaceSuccess) {
      showToast({
        type: 'success',
        title: 'Workspace Updated',
        message: 'Workspace name updated successfully.',
      });
      setWorkspaceName(currentName);
      setNameError('');
      onClose();
      dispatch(clearWorkspaceState());
    }
  }, [updateWorkspaceSuccess, showToast, dispatch, onClose, currentName]);

  useEffect(() => {
    if (updateWorkspaceError) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: updateWorkspaceError,
      });
      dispatch(clearWorkspaceState());
    }
  }, [updateWorkspaceError, showToast, dispatch]);

  const handleClose = () => {
    if (updateWorkspaceLoading) {
      return;
    }

    setWorkspaceName(currentName);
    setNameError('');
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = workspaceName.trim();

    if (!trimmedName) {
      setNameError('Workspace name is required');
      return;
    }

    if (trimmedName === currentName.trim()) {
      setNameError('Please enter a different name');
      return;
    }

    setNameError('');
    dispatch(updateWorkspaceRequest({ name: trimmedName }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Update Workspace Name"
      size="small"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Change the display name for this workspace. This will be visible to
          all team members.
        </p>

        <Input
          label="Workspace Name"
          placeholder="Enter workspace name"
          value={workspaceName}
          onChange={(e) => {
            setWorkspaceName(e.target.value);
            if (nameError) {
              setNameError('');
            }
          }}
          error={nameError}
          required
          autoFocus
          disabled={updateWorkspaceLoading}
        />

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={updateWorkspaceLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              updateWorkspaceLoading ||
              !workspaceName.trim() ||
              workspaceName.trim() === currentName.trim()
            }
            className="flex items-center gap-2"
          >
            {updateWorkspaceLoading && (
              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin shrink-0" />
            )}
            {updateWorkspaceLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UpdateWorkspaceNameModal;
